// src/components/EditCommandDialog.js
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  MenuItem,
  Alert,
  CircularProgress,
  Box,
  Autocomplete,
} from '@mui/material';
import { updateCommand } from '../services/commandService';

const defaultZones = [
  'ZONE A - LAGOS',
  'ZONE B - KADUNA',
  'ZONE B2 - KADUNA',
  'ZONE C - BAUCHI',
  'ZONE D - MINNA',
  'ZONE E - OWERRI',
  'ZONE F - IBADAN',
  'ZONE G - BENIN',
  'ZONE H - BENUE',
  'SERVICE HEADQUARTERS',
];

const defaultStates = [
  'Abia', 'Adamawa', 'Akwa Ibom', 'Anambra', 'Bauchi', 'Bayelsa', 'Benue',
  'Borno', 'Cross River', 'Delta', 'Ebonyi', 'Edo', 'Ekiti', 'Enugu',
  'FCT', 'Gombe', 'Imo', 'Jigawa', 'Kaduna', 'Kano', 'Katsina', 'Kebbi',
  'Kogi', 'Kwara', 'Lagos', 'Nasarawa', 'Niger', 'Ogun', 'Ondo', 'Osun',
  'Oyo', 'Plateau', 'Rivers', 'Sokoto', 'Taraba', 'Yobe', 'Zamfara',
];

const EditCommandDialog = ({ open, command, onClose, onSuccess, onNotify }) => {
  const [formData, setFormData] = useState({
    name: '',
    zone: '',
    state: '',
    type: '',
    status: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Merge existing zone/state into suggestions if not already present
  const [zoneOptions, setZoneOptions] = useState(defaultZones);
  const [stateOptions, setStateOptions] = useState(defaultStates);

  useEffect(() => {
    if (command) {
      setFormData({
        name: command.name,
        zone: command.zone,
        state: command.state,
        type: command.type,
        status: command.status,
      });

      // If the command's current zone isn't in defaults, add it to suggestions
      if (command.zone && !defaultZones.includes(command.zone)) {
        setZoneOptions([command.zone, ...defaultZones]);
      } else {
        setZoneOptions(defaultZones);
      }

      // Same for state
      if (command.state && !defaultStates.includes(command.state)) {
        setStateOptions([command.state, ...defaultStates]);
      } else {
        setStateOptions(defaultStates);
      }
    }
  }, [command]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    setError('');
  };

  const handleSubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    setError('');

    if (!formData.name || !formData.zone || !formData.state || !formData.type || !formData.status) {
      setError('All fields are required');
      return;
    }

    setLoading(true);

    try {
      await updateCommand(command.id, formData);
      const label = `${command?.code || ''} - ${formData.name}`.trim();
      if (onNotify) onNotify(`Command ${label} updated successfully`, 'success');
      onSuccess();
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to update command';
      setError(msg);
      if (onNotify) onNotify(msg, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setError('');
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit Command</DialogTitle>
      <DialogContent>
        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <TextField
            fullWidth
            label="Command Code"
            value={command?.code || ''}
            disabled
            margin="normal"
            helperText="Code cannot be changed"
          />

          <TextField
            fullWidth
            label="Command Name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            required
            margin="normal"
            disabled={loading}
          />

          <Autocomplete
            freeSolo
            options={zoneOptions}
            value={formData.zone}
            onChange={(e, newValue) => {
              setFormData({ ...formData, zone: newValue || '' });
              setError('');
            }}
            onInputChange={(e, newValue) => {
              setFormData({ ...formData, zone: newValue || '' });
              setError('');
            }}
            disabled={loading}
            renderInput={(params) => (
              <TextField
                {...params}
                fullWidth
                label="Zone"
                required
                margin="normal"
                placeholder="Select or type a zone name"
                helperText="Pick from suggestions or type to correct the zone name"
              />
            )}
          />

          <Autocomplete
            freeSolo
            options={stateOptions}
            value={formData.state}
            onChange={(e, newValue) => {
              setFormData({ ...formData, state: newValue || '' });
              setError('');
            }}
            onInputChange={(e, newValue) => {
              setFormData({ ...formData, state: newValue || '' });
              setError('');
            }}
            disabled={loading}
            renderInput={(params) => (
              <TextField
                {...params}
                fullWidth
                label="State"
                required
                margin="normal"
                placeholder="Select or type a state name"
                helperText="Pick from suggestions or type to correct the state name"
              />
            )}
          />

          <TextField
            fullWidth
            select
            label="Type"
            name="type"
            value={formData.type}
            onChange={handleChange}
            required
            margin="normal"
            disabled={loading}
          >
            <MenuItem value="STATE_COMMAND">State Command</MenuItem>
            <MenuItem value="SPECIAL_LOCATION">Special Location</MenuItem>
            <MenuItem value="HEADQUARTERS">Headquarters</MenuItem>
          </TextField>

          <TextField
            fullWidth
            select
            label="Status"
            name="status"
            value={formData.status}
            onChange={handleChange}
            required
            margin="normal"
            disabled={loading}
          >
            <MenuItem value="NOT_STARTED">Not Started</MenuItem>
            <MenuItem value="PENDING_OPEN">Pending Open</MenuItem>
            <MenuItem value="IN_PROGRESS">In Progress</MenuItem>
            <MenuItem value="PENDING_COMPLETION">Pending Completion</MenuItem>
            <MenuItem value="COMPLETED">Completed</MenuItem>
          </TextField>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} variant="contained" disabled={loading}>
          {loading ? <CircularProgress size={24} /> : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditCommandDialog;